import { Link, useNavigate } from '@tanstack/react-router';
import { Camera, Ellipsis, FileDown, ImagePlus, Plus, Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { BetaEndingBanner } from '../account/BetaCode.tsx';
import { useAccount } from '../account/store.ts';
import { confirmDeleteProject, importProjects, pickFile } from '../app/projectActions.ts';
import { useBlobUrl } from '../app/useBlobUrl.ts';
import { plural } from '../lib/format.ts';
import { services } from '../services/index.ts';
import { useDocument } from '../state/documentStore.ts';
import { ask } from '../state/feedback.ts';
import { useLibrary } from '../state/library.ts';
import { BACKUP_EXTENSION } from '../storage/backup.ts';
import { coverUriOf, type ProjectFile, projectTitle } from '../storage/record.ts';
import { useSync } from '../sync/service.ts';
import { Button } from '../ui/Button.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Logo } from '../ui/Logo.tsx';
import { Spinner } from '../ui/Spinner.tsx';
import { AccountButton, badgeText } from '../ui/SyncIndicator.tsx';
import { WorkspaceSwitcher } from '../ui/WorkspaceSwitcher.tsx';
import { hasCamera, newProjectFlow, pickAndStart, startFromFile } from './newProject.ts';

function details(p: ProjectFile): string {
  if (p.aiResults.length || p.exports.length)
    return `${plural(p.aiResults.length, 'render')} · ${plural(p.exports.length, 'export')}`;
  return p.doc.runs.length === 0 ? 'No light runs' : plural(p.doc.runs.length, 'light run');
}

function ProjectTile({ project, open, badge }: { project: ProjectFile; open: boolean; badge: string | null }) {
  const navigate = useNavigate();
  const cover = useBlobUrl(coverUriOf(project));
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const longPressed = useRef(false);
  const title = projectTitle(project);

  const menu = async () => {
    const choice = await ask({
      title,
      actions: [
        { label: 'Edit', value: 'edit', variant: 'primary' },
        { label: 'Rename', value: 'rename', variant: 'secondary' },
        { label: 'Delete', value: 'delete', variant: 'danger' },
        { label: 'Cancel', value: null, variant: 'ghost' },
      ],
    });
    if (choice === 'edit') await navigate({ to: '/p/$projectId/edit', params: { projectId: project.id } });
    else if (choice === 'rename')
      await navigate({ to: '/p/$projectId', params: { projectId: project.id }, search: { rename: true } });
    else if (choice === 'delete') await confirmDeleteProject(project);
  };

  return (
    <div className="group relative flex flex-col gap-1.5">
      <Link
        to="/p/$projectId"
        params={{ projectId: project.id }}
        className="flex flex-col gap-1.5 no-select transition-opacity hover:opacity-70 active:opacity-70"
        onPointerDown={() => {
          longPressed.current = false;
          timer.current = setTimeout(() => {
            longPressed.current = true;
            void menu();
          }, 500);
        }}
        onPointerUp={() => clearTimeout(timer.current)}
        onPointerLeave={() => clearTimeout(timer.current)}
        onClick={(e) => {
          if (longPressed.current) e.preventDefault();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          clearTimeout(timer.current);
          void menu();
        }}
      >
        <div className="aspect-[4/3] overflow-hidden rounded-sm bg-surface2">
          {cover ? <img src={cover} alt="" className="size-full object-cover" draggable={false} /> : null}
        </div>
        <span className="t-label truncate font-semibold">{title}</span>
        <span className="t-caption truncate text-dim">
          {open ? <span className="text-accent">Open · </span> : null}
          {details(project)}
        </span>
        {badge ? <span className="t-caption -mt-1 truncate text-muted">{badge}</span> : null}
      </Link>
      <IconButton
        icon={Ellipsis}
        label="Edit, rename or delete"
        size={32}
        tone="glass"
        className="absolute top-1.5 right-1.5 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(pointer:coarse)]:hidden"
        onClick={() => void menu()}
      />
    </div>
  );
}

export function HomeScreen() {
  const { projects: all, ready } = useLibrary();
  const { session, me, activeScope } = useAccount(
    useShallow((a) => ({ session: a.session, me: a.me, activeScope: a.activeScope })),
  );
  const rows = useSync((s) => s.rows);
  useSync((s) => s.pendingShas);
  const scope = activeScope ?? me?.personalScope ?? null;
  // Signed in: the active workspace's projects (+ this device's local-only projects in Personal).
  const projects = session
    ? all.filter((p) => {
        const row = rows[p.id];
        return row ? row.scope === scope : !me || scope === me.personalScope;
      })
    : all;
  const openId = useDocument((s) => s.projectId);
  const [busy, setBusy] = useState<null | 'library' | 'camera' | 'new'>(null);
  const [dragging, setDragging] = useState(false);
  const camera = hasCamera();

  const run = async (kind: 'library' | 'camera' | 'new', fn: () => Promise<void>) => {
    setBusy(kind);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const file = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith('image/'));
      if (file) void run('new', () => startFromFile(file));
    };
    addEventListener('paste', onPaste);
    return () => removeEventListener('paste', onPaste);
  });

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (file.type.startsWith('image/')) void run('new', () => startFromFile(file));
    else void importProjects(file);
  };

  const importBackup = async () => {
    const file = await pickFile(`${BACKUP_EXTENSION},.json,application/zip`);
    if (file) await importProjects(file);
  };

  if (!ready) return <div className="min-h-dvh bg-bg" />;

  return (
    <main
      className={`min-h-dvh overflow-y-auto ${dragging ? 'outline-2 outline-dashed outline-accent -outline-offset-8' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <div
        className="mx-auto flex max-w-[1120px] flex-col px-4"
        style={{ paddingTop: 'calc(var(--safe-top) + 8px)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
      >
        <header className="flex min-h-11 items-center gap-2">
          <Logo width={22} />
          <span className="t-heading flex-1">Better Lights</span>
          <WorkspaceSwitcher />
          <IconButton icon={FileDown} label="Import a backup (.betterlights)" onClick={() => void importBackup()} />
          <Link
            to="/settings"
            aria-label="Settings"
            title="Settings"
            className="inline-flex size-11 items-center justify-center rounded-md hover:bg-white/8"
          >
            <Settings size={20} strokeWidth={2.2} aria-hidden />
          </Link>
          <AccountButton />
        </header>
        <div className="empty:hidden pt-2">
          <BetaEndingBanner />
        </div>
        {projects.length === 0 ? (
          <div className="flex min-h-[70dvh] flex-col items-center justify-center gap-3 text-center">
            <h1 className="t-title">No projects yet</h1>
            <p className="t-body max-w-[320px] text-dim">Pick a photo of the front of a house to start.</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Button
                variant="primary"
                icon={ImagePlus}
                loading={busy === 'library'}
                disabled={!!busy}
                onClick={() => void run('library', () => pickAndStart('library'))}
              >
                Choose Photo
              </Button>
              {camera ? (
                <Button
                  icon={Camera}
                  loading={busy === 'camera'}
                  disabled={!!busy}
                  onClick={() => void run('camera', () => pickAndStart('camera'))}
                >
                  Take Photo
                </Button>
              ) : null}
            </div>
            <p className="t-caption mt-2 text-muted">You can also drop or paste a photo here.</p>
          </div>
        ) : (
          <>
            <h1 className="t-display my-4">Projects</h1>
            <div className="grid grid-cols-2 gap-x-3 gap-y-5 min-[632px]:grid-cols-3 min-[932px]:grid-cols-4">
              <button
                type="button"
                className="flex flex-col gap-1.5 text-left no-select transition-opacity hover:opacity-70"
                onClick={() => void run('new', newProjectFlow)}
                disabled={!!busy}
              >
                <div className="flex aspect-[4/3] items-center justify-center rounded-sm bg-surface2 text-dim">
                  {busy ? <Spinner size={24} /> : <Plus size={28} strokeWidth={1.75} aria-hidden />}
                </div>
                <span className="t-label font-semibold">New Project</span>
                <span className="t-caption text-dim">{camera ? 'From a photo or the camera' : 'From a photo'}</span>
              </button>
              {projects.map((p) => (
                <ProjectTile
                  key={p.id}
                  project={p}
                  open={p.id === openId}
                  badge={session ? badgeText(services.sync.badgeFor(p)) : null}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
